import { useCallback, useEffect, useMemo, useRef } from "react";

import { registerDismissable } from "./dismissableRegistry";

export interface UsePopoverOptions {
  /** React owns the truth; the hook mirrors it onto showPopover/hidePopover. */
  open: boolean;
  /** Called when the platform closes the popover behind React's back (light dismiss, Escape). */
  onOpenChange: (open: boolean) => void;
  /**
   * "auto" gets light dismiss and one-at-a-time behaviour; "manual" gets
   * neither, and so is also not counted in the dismissable registry. Defaults
   * to "auto".
   */
  mode?: "auto" | "manual";
}

export interface UsePopoverResult {
  popoverRef: React.RefObject<HTMLDivElement | null>;
  popoverProps: {
    popover: "auto" | "manual";
    onToggle: React.ToggleEventHandler<HTMLDivElement>;
  };
}

/**
 * Drives a Popover API surface from React state and reports platform-initiated
 * closes (light dismiss, Escape, another auto popover opening) back through
 * `onOpenChange`, which is the only way React hears about them.
 *
 * Spread `popoverProps` onto the floating element and attach `popoverRef` to it.
 */
export function usePopover({
  open,
  onOpenChange,
  mode = "auto",
}: UsePopoverOptions): UsePopoverResult {
  const popoverRef = useRef<HTMLDivElement>(null);

  const openRef = useRef(open);
  const onOpenChangeRef = useRef(onOpenChange);
  useEffect(() => {
    openRef.current = open;
    onOpenChangeRef.current = onOpenChange;
  });

  /* Count this popover while it is up, so a host's global Escape handling knows
     an Escape that light-dismisses it belongs to the popover. The platform can
     close a popover behind React's back, and the count only drops once that has
     been reported through onOpenChange and re-rendered - which is exactly the
     lag the Escape guard needs.

     "auto" is the whole condition, because the registry answers exactly one
     question - does an Escape right now get consumed by this overlay? - and
     light dismiss is what makes the answer yes. A "manual" popover has no light
     dismiss and no Escape handling of its own: it is up because its host says
     so (e.g. shown on hover/focus, hidden on mouse-leave/blur), and Escape
     passes straight through it. Counting one would be a standing lie for as
     long as it showed, and the host reading the count would treat every
     Escape while such a popover is up as an overlay dismissing itself - which
     in Companion means Esc-Esc never reaches switch-user. A manual popover
     whose host does consume Escape has to say so the way any non-kit overlay
     does, with `registerDismissable`. */
  useEffect(() => {
    if (!open || mode !== "auto") return undefined;
    return registerDismissable();
  }, [open, mode]);

  useEffect(() => {
    const element = popoverRef.current;
    if (!element) return;
    try {
      const isShown = element.matches(":popover-open");
      if (open && !isShown) element.showPopover();
      else if (!open && isShown) element.hidePopover();
    } catch {
      /* Throws when the element is not connected or has no popover attribute yet
         (both transient); the next commit re-runs this and settles it. */
    }
  }, [open]);

  const handleToggle = useCallback(
    (event: React.ToggleEvent<HTMLDivElement>) => {
      if (event.newState === "closed" && openRef.current)
        onOpenChangeRef.current(false);
    },
    [],
  );

  const popoverProps = useMemo(
    () => ({ popover: mode, onToggle: handleToggle }),
    [mode, handleToggle],
  );

  return { popoverRef, popoverProps };
}
